/**
 * All the best — flower elastic entrance on scroll + lid video play (GSAP + ScrollTrigger).
 * Depends on assets/cu-window.js (layout/theme.liquid).
 *
 * Behaviour: lid video plays ONCE the first time the section scrolls into view,
 * then holds on its final frame. It does not replay on subsequent scrolls.
 * Flowers also animate in only once.
 */
(function () {
  function loadScrollTrigger() {
    if (window.CU && typeof window.CU.loadScrollTrigger === 'function') {
      return window.CU.loadScrollTrigger();
    }
    return Promise.reject(new Error('CU All the best: cu-window.js not loaded.'));
  }

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /**
   * Seek the video to frame 0 once metadata is loaded so mobile browsers
   * (iOS Chrome/Safari) paint the first frame even before play() is called.
   * Without this, a muted non-autoplay video can render as 0×0 and stay
   * invisible until ScrollTrigger fires.
   */
  function primeVideoFirstFrame(video) {
    if (!video) return;
    if (video.dataset.cuPrimed === 'true') return;

    function seekToZero() {
      try {
        video.currentTime = 0;
      } catch (e) {}
      video.dataset.cuPrimed = 'true';
    }

    if (video.readyState >= 1) {
      seekToZero();
    } else {
      video.addEventListener('loadedmetadata', seekToZero, { once: true });
      try {
        video.load();
      } catch (e) {}
    }
  }

  function playVideoOnce(video) {
    if (!video) return;
    if (video.dataset.cuPlayed === 'true') return;

    function tryPlay() {
      try {
        video.currentTime = 0;
      } catch (e) {}
      video.muted = true;
      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(function () {
          video.dataset.cuPlayed = 'true';
        }).catch(function () {
          // play() rejected (e.g. autoplay policy) — leave flag unset so a
          // future trigger can try again.
        });
      } else {
        video.dataset.cuPlayed = 'true';
      }
    }

    if (video.readyState >= 2) {
      tryPlay();
    } else {
      video.addEventListener('canplay', tryPlay, { once: true });
    }
  }

  /**
   * @param {HTMLElement} root — [data-cu-allthebest-root]
   */
  function initCuAllTheBest(root) {
    if (!root || root.dataset.cuAllthebestReady === 'true') {
      return;
    }

    var enable =
      root.getAttribute('data-enable-animation') !== 'false';

    var video =
      root.querySelector('video') ||
      root.querySelector('[data-allthebest-video]');

    // Paint first frame on mobile before any scroll trigger fires.
    primeVideoFirstFrame(video);

    if (!enable) {
      root.dataset.cuAllthebestReady = 'true';
      return;
    }

    if (prefersReducedMotion()) {
      root.dataset.cuAllthebestReady = 'true';
      return;
    }

    var flowers = root.querySelectorAll('.allthebest_flower');
    var triggerEl = root.querySelector('.allthebest_flower-container');

    if (!triggerEl || !flowers.length) {
      root.dataset.cuAllthebestReady = 'true';
      return;
    }

    var scrollStart =
      root.getAttribute('data-scroll-start') || 'top 70%';
    var duration = parseFloat(
      root.getAttribute('data-flower-duration') || '1.5'
    );
    var stagger = parseFloat(
      root.getAttribute('data-flower-stagger') || '0.1'
    );
    var delay = parseFloat(
      root.getAttribute('data-flower-delay') || '1'
    );

    function setupGsap(gsap) {
      if (!root.isConnected) {
        return;
      }

      if (root._cuAllTheBestCtx) {
        root._cuAllTheBestCtx.revert();
        root._cuAllTheBestCtx = null;
      }

      if (isNaN(duration) || duration <= 0) {
        duration = 1.5;
      }
      if (isNaN(stagger) || stagger < 0) {
        stagger = 0.1;
      }
      if (isNaN(delay) || delay < 0) {
        delay = 1;
      }

      gsap.config({ nullTargetWarn: false });

      var ctxScope = root.closest('.section_allthebest') || root;
      var flowersAnimated = false;

      var ctx = gsap.context(function () {
        gsap.set(flowers, { scale: 0, transformOrigin: '50% 50%' });

        function onFirstView() {
          // Fire video + flowers once, then disable the trigger so nothing
          // re-plays on subsequent scrolls.
          playVideoOnce(video);
          if (!flowersAnimated) {
            flowersAnimated = true;
            gsap.to(flowers, {
              scale: 1,
              duration: duration,
              ease: 'elastic.out(0.4, 0.3)',
              stagger: stagger,
              delay: delay,
            });
          }
          if (trigger) {
            trigger.kill();
          }
        }

        var trigger = ScrollTrigger.create({
          trigger: triggerEl,
          start: scrollStart,
          once: true,
          onEnter: onFirstView,
        });
      }, ctxScope);

      root._cuAllTheBestCtx = ctx;
      root.dataset.cuAllthebestReady = 'true';
    }

    var setupStarted = false;

    function run() {
      if (setupStarted) {
        return;
      }
      setupStarted = true;
      loadScrollTrigger()
        .then(function (gsap) {
          setupGsap(gsap);
        })
        .catch(function () {
          for (var j = 0; j < flowers.length; j++) {
            flowers[j].style.transform = '';
          }
          root.dataset.cuAllthebestReady = 'true';
        });
    }

    function onVideoReady() {
      run();
    }

    if (!video) {
      onVideoReady();
      return;
    }

    function tryInitFromVideo() {
      if (video.readyState >= 3) {
        onVideoReady();
        return true;
      }
      return false;
    }

    if (tryInitFromVideo()) {
      return;
    }

    video.addEventListener('loadeddata', onVideoReady, { once: true });

    window.addEventListener(
      'load',
      function () {
        if (root.dataset.cuAllthebestReady === 'true') {
          return;
        }
        if (tryInitFromVideo()) {
          return;
        }
        setTimeout(function () {
          if (root.dataset.cuAllthebestReady === 'true') {
            return;
          }
          onVideoReady();
        }, 1500);
      },
      { once: true }
    );
  }

  function destroyCuAllTheBest(root) {
    if (!root) {
      return;
    }
    if (root._cuAllTheBestCtx) {
      root._cuAllTheBestCtx.revert();
      root._cuAllTheBestCtx = null;
    }
    delete root.dataset.cuAllthebestReady;
  }

  function scanAndInit(container) {
    var scope = container || document;
    var nodes = scope.querySelectorAll('[data-cu-allthebest-root]');
    for (var i = 0; i < nodes.length; i++) {
      initCuAllTheBest(nodes[i]);
    }
  }

  function registerGlobalListenersOnce() {
    if (window.__cuAllTheBestListeners) {
      return;
    }
    window.__cuAllTheBestListeners = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        scanAndInit();
      });
    }

    document.addEventListener('shopify:section:load', function (event) {
      scanAndInit(event.target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var roots = event.target.querySelectorAll('[data-cu-allthebest-root]');
      for (var i = 0; i < roots.length; i++) {
        destroyCuAllTheBest(roots[i]);
      }
    });
  }

  registerGlobalListenersOnce();
  scanAndInit();
})();